'use client';

import { useState } from 'react';
import { useLocale, useTranslations } from 'next-intl';
import { usePathname, useRouter } from '@boty/i18n/navigation';
import { locales, type Locale } from '@boty/i18n';
import IconButton from '@mui/material/IconButton';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Tooltip from '@mui/material/Tooltip';
import LanguageIcon from '@mui/icons-material/Language';
import { saveLocalePreference } from '../../lib/useAutoLocale';

/**
 * Compact language switcher for the consumer bot page footer.
 * Globe icon that opens a dropdown menu with supported locales.
 * On switch, persists choice to localStorage and navigates to the new locale URL.
 */
export function LanguageSwitcher() {
  const t = useTranslations('common.language');
  const tSwitcher = useTranslations('common.languageSwitcher');
  const currentLocale = useLocale() as Locale;
  const pathname = usePathname();
  const router = useRouter();

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const handleLocaleChange = (newLocale: Locale) => {
    setAnchorEl(null);
    if (newLocale === currentLocale) return;

    saveLocalePreference(newLocale);
    router.push(pathname, { locale: newLocale });
  };

  return (
    <>
      <Tooltip title={tSwitcher('tooltip')} arrow>
        <IconButton
          onClick={(e) => setAnchorEl(e.currentTarget)}
          size="small"
          aria-label={tSwitcher('ariaLabel')}
          aria-expanded={open ? 'true' : undefined}
          aria-haspopup="true"
          sx={{
            color: 'text.secondary',
            opacity: 0.6,
            p: 0.5,
            '&:hover': { opacity: 1 },
          }}
        >
          <LanguageIcon sx={{ fontSize: 16 }} />
        </IconButton>
      </Tooltip>
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        transformOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        {locales.map((locale) => (
          <MenuItem
            key={locale}
            onClick={() => handleLocaleChange(locale)}
            selected={locale === currentLocale}
          >
            {t(locale)}
          </MenuItem>
        ))}
      </Menu>
    </>
  );
}
